import { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Slider } from '@/components/ui/slider';
import IkiSymbol from '@/components/IkiSymbol';
import { MoodGlyph, Wave, Leaf } from '@/components/HypIcons';
import CrisisScreen from '@/components/CrisisScreen';
import { detectCrisis } from '@/hooks/useCrisisDetection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Pattern Intelligence Panel ────────────────────────────────────────────────
const VALENCE_COLOR = {
  positive:   { bg: 'rgba(30,107,104,0.06)',  border: '#2D8B87', dot: '#2D8B87' },
  neutral:    { bg: 'rgba(184,211,207,0.1)',  border: '#B8D3CF', dot: '#9AADAB' },
  attention:  { bg: 'rgba(201,152,62,0.06)',  border: '#C9983E', dot: '#C9983E' },
};

function PatternIntelligencePanel({ userId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/intelligence/patterns/${userId}${force ? '?force_refresh=true' : ''}`
      );
      setData(res.data);
      if (res.data?.ready) setOpen(true);
    } catch {
      // backend offline — silent fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); }, [userId]); // eslint-disable-line

  const notReady = data && !data.ready;
  const daysCompleted = data?.days_completed ?? 0;
  const daysNeeded = 7;
  const progressPct = notReady ? Math.min(100, Math.round((daysCompleted / daysNeeded) * 100)) : 100;

  return (
    <div style={{
      marginTop: '32px',
      background: 'rgba(255,255,255,0.9)',
      borderRadius: '18px',
      border: '1px solid rgba(184,211,207,0.3)',
      overflow: 'hidden',
      boxShadow: '0 2px 16px rgba(30,107,104,0.06)',
    }}>
      {/* Header */}
      <button
        onClick={() => data?.ready && setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '18px 20px', cursor: data?.ready ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', gap: '12px', textAlign: 'left',
        }}
      >
        {/* Icon / progress ring when not ready */}
        {notReady ? (
          <div style={{ position: 'relative', width: '38px', height: '38px', flexShrink: 0 }}>
            <svg width="38" height="38" viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="15" fill="none" stroke="rgba(184,211,207,0.25)" strokeWidth="2.5"/>
              <circle cx="19" cy="19" r="15" fill="none" stroke="#2D8B87" strokeWidth="2.5"
                strokeDasharray={`${(progressPct / 100) * 94.25} 94.25`}
                strokeLinecap="round"
                style={{ transform: 'rotate(-90deg)', transformOrigin: '19px 19px', transition: 'stroke-dasharray 0.5s ease' }}
              />
            </svg>
            <span style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', fontWeight: '700', color: '#1E6B68',
            }}>{daysCompleted}/{daysNeeded}</span>
          </div>
        ) : (
          <div style={{
            width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(30,107,104,0.1), rgba(45,139,135,0.08))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="2" stroke="#1E6B68" strokeWidth="1.4"/>
              <path d="M10 3 V6 M10 14 V17 M3 10 H6 M14 10 H17" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M5.5 5.5 L7.5 7.5 M12.5 12.5 L14.5 14.5 M5.5 14.5 L7.5 12.5 M12.5 7.5 L14.5 5.5" stroke="#1E6B68" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
            </svg>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: '700', fontSize: '0.9rem', color: '#1E3A3A' }}>Pattern Intelligence</p>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9AADAB', lineHeight: 1.4 }}>
            {loading ? 'Scanning your data…' :
             notReady ? `${daysNeeded - daysCompleted} more loop${daysNeeded - daysCompleted !== 1 ? 's' : ''} to unlock your emotional fingerprint` :
             data?.summary ?? 'AI analysis of your emotional patterns'}
          </p>
        </div>
        {data?.ready && !loading && (
          <span style={{ color: '#B8D3CF', fontSize: '0.75rem', flexShrink: 0 }}>
            {open ? '▲' : '▼'}
          </span>
        )}
      </button>

      {/* Loading dots */}
      {loading && (
        <div style={{ padding: '4px 20px 18px', display: 'flex', gap: '5px', alignItems: 'center' }}>
          {[0, 0.2, 0.4].map((d, i) => (
            <div key={i} style={{
              width: '5px', height: '5px', borderRadius: '50%', background: '#2D8B87',
              animation: `mmDotPulse 1.2s ease-in-out ${d}s infinite`,
            }} />
          ))}
          <style>{`@keyframes mmDotPulse{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}`}</style>
          <span style={{ fontSize: '0.78rem', color: '#9AADAB', marginLeft: '6px' }}>Analysing your patterns…</span>
        </div>
      )}

      {/* Patterns expanded */}
      {!loading && data?.ready && open && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(184,211,207,0.2)' }}>
          <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(data.patterns || []).map((p, i) => {
              const v = VALENCE_COLOR[p.valence] || VALENCE_COLOR.neutral;
              return (
                <div key={p.id || i} style={{
                  padding: '14px 16px',
                  background: v.bg,
                  borderRadius: '12px',
                  borderLeft: `3px solid ${v.border}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: v.dot, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.65rem', fontWeight: '700', color: v.dot, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{p.category}</span>
                  </div>
                  <p style={{ margin: '0 0 6px', fontWeight: '600', fontSize: '0.88rem', color: '#1E3A3A' }}>{p.title}</p>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>{p.insight}</p>
                  {p.data_points?.length > 0 && (
                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {p.data_points.map((dp, j) => (
                        <p key={j} style={{ margin: 0, fontSize: '0.72rem', color: '#888', lineHeight: 1.4 }}>· {dp}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => load(true)}
              disabled={loading}
              style={{
                background: 'none', border: 'none',
                color: '#9AADAB', fontSize: '0.75rem', cursor: 'pointer', padding: '4px 0',
              }}
            >
              ↺ Refresh analysis
            </button>
            {data?.generated_at && (
              <span style={{ fontSize: '0.7rem', color: '#C5D5D3' }}>
                Updated {data.generated_at}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MindMirror() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedEntryId, setExpandedEntryId] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [dayPrompt, setDayPrompt] = useState(null);   // {prompt, source, context_label}
  const [mirrorLine, setMirrorLine] = useState(null); // one-line AI reflection after save
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    mood: 5,
    energy: 5,
    focus: 5,
    body_sensation: '',
    activation_location: '',
    what_emerged: '',
    integration_insight: '',
    entry_text: '',       // composed from structured fields on save
    synchronicity: ''
  });

  const loadEntries = async (userId) => {
    if (!userId) return;
    
    try {
      // Add cache-busting timestamp to prevent stale data
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/journal-entries?user_id=${userId}&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error loading entries:', error);
    }
  };

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.user_id) {
          setUser(parsedUser);
          loadEntries(parsedUser.user_id);
          setIsLoadingAuth(false);
        } else {
          toast.error('Please log in to access your journal');
          // Wait a moment for the user to see the message, then redirect
          setTimeout(() => {
            navigate('/');
          }, 1500);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        toast.error('Session expired. Please log in again.');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } else {
      toast.error('Please log in to access your journal');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    }
  }, []);

  const [showCrisisScreen, setShowCrisisScreen] = useState(false);

  const handleSubmit = async () => {
    // Compose structured entry_text from somatic prompts
    const parts = [];
    if (formData.body_sensation.trim())       parts.push(`Body: ${formData.body_sensation.trim()}`);
    if (formData.activation_location.trim())  parts.push(`Sensation: ${formData.activation_location.trim()}`);
    if (formData.what_emerged.trim())         parts.push(`Emerged: ${formData.what_emerged.trim()}`);
    if (formData.integration_insight.trim())  parts.push(`Integration: ${formData.integration_insight.trim()}`);
    if (formData.entry_text.trim())           parts.push(formData.entry_text.trim());
    const composedText = parts.join('\n\n');

    if (!composedText.trim()) {
      toast.error('Please complete at least one reflection prompt.');
      return;
    }

    if (!user?.user_id) {
      toast.error('User not found. Please log in again.');
      return;
    }

    // Crisis check before saving journal entry
    if (detectCrisis(composedText)) {
      setShowCrisisScreen(true);
      return;
    }

    try {
      const entryData = {
        ...formData,
        entry_text: composedText,
        user_id: user.user_id
      };
      const saveRes = await axios.post(`${API}/journal-entries`, entryData);
      setMirrorLine(saveRes.data?.mirror || null);
      toast.success('Entry saved.');
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        mood: 5, energy: 5, focus: 5,
        body_sensation: '', activation_location: '',
        what_emerged: '', integration_insight: '',
        entry_text: '', synchronicity: ''
      });
      loadEntries(user.user_id);
    } catch (error) {
      toast.error('Failed to save entry');
      console.error('Error saving entry:', error);
    }
  };

  // Context-aware prompt of the day — the page asks, instead of opening blank
  useEffect(() => {
    if (!user?.user_id) return;
    axios.get(`${API}/journal/prompt-of-day/${encodeURIComponent(user.user_id)}`)
      .then(r => setDayPrompt(r.data))
      .catch(() => {});
  }, [user]);

  // ── Voice journaling — speak instead of type ─────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 1000) return; // accidental tap
        setIsTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'journal.webm');
          const r = await axios.post(
            `${API}/journal/transcribe?user_id=${encodeURIComponent(user.user_id)}`, fd);
          const text = r.data?.text?.trim();
          if (text) {
            setFormData(prev => ({
              ...prev,
              entry_text: prev.entry_text ? `${prev.entry_text.trim()}\n${text}` : text,
            }));
            toast.success('Transcribed — review and edit freely.');
          } else {
            toast.error("Couldn't hear anything — try again a little closer to the mic.");
          }
        } catch {
          toast.error('Transcription failed. You can type instead.');
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setIsRecording(true);
    } catch {
      toast.error('Microphone unavailable — check permissions.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const moodIcons = ['sadness', 'presence', 'calm', 'calm', 'gratitude', 'joy', 'insight', 'sparkles', 'joy', 'rocket'];
  
  // Show loading screen while checking authentication
  if (isLoadingAuth) {
    return (
      <div className="page-container" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.2rem', color: '#1E6B68' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page-container" data-testid="mind-mirror-page">
      {showCrisisScreen && (
        <CrisisScreen
          onDismiss={() => setShowCrisisScreen(false)}
        />
      )}
      {/* Header — compact, so the prompt of the day sits near the top */}
      <div style={{
        textAlign: 'center',
        padding: '18px 20px',
        background: 'linear-gradient(135deg, rgba(184, 211, 207, 0.1) 0%, rgba(168, 230, 206, 0.1) 100%)',
        borderRadius: '20px',
        maxWidth: '900px',
        margin: '0 auto 20px'
      }}>
        <div style={{ marginBottom: '8px' }}>
          <IkiSymbol name="journal" size={30} />
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.7rem',
          color: '#1E6B68',
          marginBottom: '6px'
        }}>
          Journal
        </h1>
        <p style={{
          color: '#2D8B87',
          fontSize: '0.92rem',
          lineHeight: '1.5',
          maxWidth: '440px',
          margin: '0 auto'
        }}>
          Structured somatic reflection. What you track, you transform.
        </p>
        <p style={{
          fontSize: '0.72rem',
          color: '#9AADAB',
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          maxWidth: '420px',
          margin: '10px auto 0',
          lineHeight: '1.5'
        }}>
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '2px' }}>
            <svg width="11" height="11" viewBox="0 0 20 20" fill="none"><rect x="4" y="9" width="12" height="9" rx="2.5" stroke="#8FA3A0" strokeWidth="1.6"/><path d="M7 9V6.5a3 3 0 0 1 6 0V9" stroke="#8FA3A0" strokeWidth="1.6" strokeLinecap="round"/></svg>
          </span>
          Your journal entries are private and never shared with practitioners.{' '}
          <a href="/privacy" style={{ color: '#2D8B87', textDecoration: 'underline', textUnderlineOffset: '2px' }}>Privacy policy</a>
        </p>
      </div>

      {/* The mirror — one line reflected back after saving */}
      {mirrorLine && (
        <div style={{
          maxWidth: '900px', margin: '0 auto 24px', padding: '20px 24px',
          background: 'linear-gradient(135deg, rgba(30,107,104,0.06), rgba(184,211,207,0.14))',
          borderRadius: '18px', border: '1px solid rgba(30,107,104,0.18)',
          display: 'flex', gap: '14px', alignItems: 'flex-start',
        }}>
          <div style={{ flexShrink: 0, marginTop: 2 }}><IkiSymbol name="insight" size={20} /></div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 4px', fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2D8B87' }}>
              Reflected back
            </p>
            <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.02rem', color: '#1E3A3A', lineHeight: 1.6 }}>
              {mirrorLine}
            </p>
          </div>
          <button onClick={() => setMirrorLine(null)} aria-label="Dismiss reflection"
            style={{ background: 'none', border: 'none', color: '#B8D3CF', fontSize: '1rem', cursor: 'pointer', padding: '2px' }}>×</button>
        </div>
      )}

      {/* Prompt of the day — a specific question instead of a blank page */}
      {dayPrompt && !showForm && (
        <div style={{
          maxWidth: '900px', margin: '0 auto 24px', padding: '22px 24px',
          background: 'rgba(255,255,255,0.85)', borderRadius: '18px',
          border: '1px solid rgba(184,211,207,0.55)',
          boxShadow: '0 2px 14px rgba(30,107,104,0.05)',
        }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2D8B87' }}>
            {dayPrompt.context_label}
          </p>
          <p style={{ margin: '0 0 14px', fontFamily: "'Playfair Display', serif", fontSize: '1.08rem', color: '#1E3A3A', lineHeight: 1.65 }}>
            {dayPrompt.prompt}
          </p>
          <button
            onClick={() => { setShowForm(true); setTimeout(() => document.getElementById('journal-form-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
            style={{
              padding: '10px 22px', background: 'rgba(30,107,104,0.08)',
              color: '#1E6B68', border: '1.5px solid rgba(30,107,104,0.3)',
              borderRadius: '18px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
            }}
          >Reflect on this →</button>
        </div>
      )}

      {/* Primary Action - New Entry */}
      <div style={{ maxWidth: '900px', margin: '0 auto 48px' }}>
        <button
          onClick={() => { setShowForm(true); setTimeout(() => document.getElementById('journal-form-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
          style={{
            width: '100%',
            padding: '24px',
            background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '20px',
            fontSize: '1.2rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(30, 107, 104, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(30, 107, 104, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(30, 107, 104, 0.3)';
          }}
          data-testid="new-entry-button"
        >
          <IkiSymbol name="edit" size={24} />
          <span>Create New Entry</span>
        </button>
      </div>

      {/* Secondary Action - Your Growth */}
      <div style={{ maxWidth: '900px', margin: '0 auto 48px' }}>
        <button
          onClick={() => navigate('/insights')}
          style={{
            width: '100%',
            padding: '20px',
            background: 'rgba(255, 255, 255, 0.8)',
            color: '#1E6B68',
            border: '2px solid #B8D3CF',
            borderRadius: '20px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(184, 211, 207, 0.2)';
            e.currentTarget.style.borderColor = '#1E6B68';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
            e.currentTarget.style.borderColor = '#B8D3CF';
          }}
        >
          <IkiSymbol name="insight" size={24} />
          <span>Your Insights</span>
        </button>
      </div>

      {/* Emotional Trends — mood/energy/focus across recent entries */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto 48px',
        padding: entries.length >= 3 ? '24px 20px 12px' : '32px',
        background: 'rgba(255, 255, 255, 0.75)',
        borderRadius: '20px',
        border: '1px solid rgba(184, 211, 207, 0.5)',
      }}>
        {entries.length >= 3 ? (
          <>
            <h3 style={{ color: '#1E6B68', fontSize: '1.05rem', margin: '0 0 2px', fontFamily: "'Playfair Display', serif" }}>
              Emotional Trends
            </h3>
            <p style={{ color: '#9AADAB', fontSize: '0.75rem', margin: '0 0 14px' }}>
              Mood, energy and focus across your last {Math.min(entries.length, 21)} entries.
            </p>
            <div style={{ width: '100%', height: 190 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[...entries].slice(0, 21).reverse().map(e => ({
                    date: (e.date || '').slice(5).split('-').reverse().join('/'),
                    Mood: e.mood, Energy: e.energy, Focus: e.focus,
                  }))}
                  margin={{ top: 4, right: 8, left: -26, bottom: 0 }}
                >
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9AADAB' }} tickLine={false} axisLine={{ stroke: 'rgba(184,211,207,0.5)' }} />
                  <YAxis domain={[1, 10]} ticks={[1, 5, 10]} tick={{ fontSize: 10, fill: '#9AADAB' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid rgba(30,107,104,0.15)', fontSize: '0.78rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.72rem' }} iconType="plainline" />
                  <Line type="monotone" dataKey="Mood" stroke="#1E6B68" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Energy" stroke="#4AACA8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Focus" stroke="#B8906A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <IkiSymbol name="sparkles" size={32} />
            <h3 style={{ color: '#1E6B68', fontSize: '1.3rem', marginTop: '16px', marginBottom: '8px' }}>
              Emotional Trends
            </h3>
            <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: '1.6' }}>
              {entries.length === 0
                ? 'Your mood, energy and focus will be charted here — starting with your third entry.'
                : `${3 - entries.length} more ${entries.length === 2 ? 'entry' : 'entries'} and your trends appear here.`}
            </p>
          </div>
        )}
      </div>
      
      {/* Recent Entries Section */}
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.8rem',
          color: '#1E6B68',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          Your Journal Entries
        </h2>

        {/* Entry Form */}
      {showForm && (
        <div id="journal-form-anchor" className="glass-card mb-4" data-testid="entry-form">
          <h2>Today&apos;s Reflection</h2>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#1E6B68' }}>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #D9CBA3',
                fontSize: '16px',
                fontFamily: 'Manrope, sans-serif'
              }}
              data-testid="entry-date"
            />
          </div>

          {/* Mood Slider */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontWeight: '500', color: 'var(--iki-turquoise)' }}>
              Mood:
              <MoodGlyph level={formData.mood} size={28} color="#1E6B68" isSelected={true} />
              <span style={{ fontSize: '0.9rem', color: '#1E6B68', fontWeight: '600' }}>{formData.mood}/10</span>
            </label>
            <Slider
              value={[formData.mood]}
              onValueChange={(value) => setFormData({ ...formData, mood: value[0] })}
              min={1}
              max={10}
              step={1}
              className="mood-slider-custom"
              data-testid="mood-slider"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--iki-text-muted)' }}>
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          {/* Energy Slider */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', color: '#1E6B68' }}>
              Energy: {formData.energy}/10
            </label>
            <Slider
              value={[formData.energy]}
              onValueChange={(value) => setFormData({ ...formData, energy: value[0] })}
              min={1}
              max={10}
              step={1}
              data-testid="energy-slider"
            />
          </div>

          {/* Focus Slider */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', color: '#1E6B68' }}>
              Focus: {formData.focus}/10
            </label>
            <Slider
              value={[formData.focus]}
              onValueChange={(value) => setFormData({ ...formData, focus: value[0] })}
              min={1}
              max={10}
              step={1}
              data-testid="focus-slider"
            />
          </div>

          {/* The question being answered, kept in view while writing */}
          {dayPrompt && (
            <div style={{
              marginBottom: '18px', padding: '12px 16px',
              background: 'rgba(30,107,104,0.05)', borderRadius: '12px',
              borderLeft: '3px solid rgba(30,107,104,0.35)',
            }}>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#3A5754', lineHeight: 1.6, fontStyle: 'italic' }}>
                {dayPrompt.prompt}
              </p>
            </div>
          )}

          {/* Structured Somatic Integration Prompts */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,211,207,0.1) 100%)',
            borderRadius: '16px', padding: '20px', marginBottom: '24px',
            borderLeft: '3px solid #B8D3CF'
          }}>
            <div style={{
              fontSize: '0.72rem', color: '#8FA3A0', letterSpacing: '0.1em',
              textTransform: 'uppercase', fontWeight: '600', marginBottom: '18px',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <Wave size={12} color="#8FA3A0" />
              Integration prompts
            </div>

            {[
              { key: 'body_sensation',      label: 'Body',        placeholder: 'What did you notice in your body today? Quality, location, texture of sensation.', testId: 'body-sensation' },
              { key: 'activation_location', label: 'Activation',  placeholder: 'Where did you feel activation? What was its quality — tightness, heat, movement, stillness?', testId: 'activation-loc' },
              { key: 'what_emerged',        label: 'What emerged', placeholder: 'What surfaced during practice or today that hasn\'t fully settled yet?', testId: 'what-emerged' },
              { key: 'integration_insight', label: 'Integration', placeholder: 'What insight, shift, or intention are you carrying forward from today?', testId: 'integration-insight' }
            ].map(prompt => (
              <div key={prompt.key} style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block', fontSize: '0.8rem', fontWeight: '700',
                  color: '#1E6B68', marginBottom: '6px', letterSpacing: '0.03em'
                }}>
                  {prompt.label}
                </label>
                <textarea
                  value={formData[prompt.key]}
                  onChange={(e) => setFormData({ ...formData, [prompt.key]: e.target.value })}
                  placeholder={prompt.placeholder}
                  rows={2}
                  data-testid={prompt.testId}
                  style={{
                    width: '100%', padding: '12px 14px',
                    borderRadius: '10px', border: '1px solid rgba(184,211,207,0.5)',
                    fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical',
                    background: 'rgba(255,255,255,0.8)', color: '#333',
                    lineHeight: '1.5'
                  }}
                />
              </div>
            ))}
          </div>

          {/* Free note — optional, typed or spoken */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#1E6B68' }}>
                Anything else <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span>
              </label>
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                aria-label={isRecording ? 'Stop recording' : 'Speak your entry'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '6px 14px', borderRadius: '14px',
                  background: isRecording ? 'rgba(192,87,70,0.1)' : 'rgba(30,107,104,0.07)',
                  border: isRecording ? '1.5px solid rgba(192,87,70,0.45)' : '1.5px solid rgba(30,107,104,0.25)',
                  color: isRecording ? '#B0554A' : '#1E6B68',
                  fontSize: '0.75rem', fontWeight: '600',
                  cursor: isTranscribing ? 'wait' : 'pointer',
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: isRecording ? '#C05746' : '#2D8B87',
                  animation: isRecording ? 'pulse 1.2s ease-in-out infinite' : 'none',
                }} />
                {isTranscribing ? 'Transcribing…' : isRecording ? 'Stop — I\'m done' : 'Speak instead'}
              </button>
            </div>
            <textarea
              value={formData.entry_text}
              onChange={(e) => setFormData({ ...formData, entry_text: e.target.value })}
              placeholder="Any other reflection, thought, or observation..."
              rows={3}
              data-testid="entry-text"
              style={{
                width: '100%', padding: '12px 14px',
                borderRadius: '10px', border: '1px solid rgba(184,211,207,0.4)',
                fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical',
                background: 'rgba(255,255,255,0.8)', color: '#333'
              }}
            />
          </div>

          {/* Synchronicity */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#1E6B68', marginBottom: '6px' }}>
              Synchronicities <span style={{ fontWeight: '400', color: '#AAB8B6' }}>(optional)</span>
            </label>
            <textarea
              value={formData.synchronicity}
              onChange={(e) => setFormData({ ...formData, synchronicity: e.target.value })}
              placeholder="Meaningful coincidences, patterns, or images from today."
              rows={2}
              data-testid="synchronicity-text"
              style={{
                width: '100%', padding: '12px 14px',
                borderRadius: '10px', border: '1px solid rgba(184,211,207,0.4)',
                fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical',
                background: 'rgba(255,255,255,0.8)', color: '#333'
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={handleSubmit} data-testid="save-entry-btn" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IkiSymbol name="save" size="md" ariaLabel="Save" />
              Save Entry
            </button>
            <button 
              className="btn btn-ghost" 
              onClick={() => setShowForm(false)}
              data-testid="cancel-entry-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries List */}
      {entries.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '80px 32px',
          background: 'rgba(255, 255, 255, 0.5)',
          borderRadius: '24px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <div style={{ marginBottom: '24px' }}>
            <IkiSymbol name="journal" size={56} />
          </div>
          <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.8rem',
            color: '#1E6B68',
            marginBottom: '16px'
          }}>
            Your journey begins here
          </h3>
          <p style={{ 
            color: '#666', 
            fontSize: '1.05rem',
            lineHeight: '1.7',
            marginBottom: '32px'
          }}>
            Each entry is a mirror of your inner world.<br />
            Start writing to discover patterns, track growth,<br />
            and honor your daily experience.
          </p>
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: '16px 32px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '24px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <IkiSymbol name="edit" size={20} />
            Write Your First Entry
          </button>
        </div>
      ) : (
        <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="glass-card"
                    data-testid={`entry-${entry.id}`}
                    onClick={() => setExpandedEntryId(id => id === entry.id ? null : entry.id)}
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      border: expandedEntryId === entry.id ? '1.5px solid rgba(30,107,104,0.3)' : undefined
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 107, 104, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(30, 107, 104, 0.1)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ 
                          fontSize: '1.1rem', 
                          fontWeight: '600', 
                          color: '#1E6B68',
                          marginBottom: '4px'
                        }}>
                          {new Date(entry.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#999' }}>
                          {new Date(entry.created_at).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      <div>
                        <MoodGlyph level={entry.mood} size={36} color="#1E6B68" isSelected={true} />
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '16px', 
                      marginBottom: '12px',
                  padding: '16px',
                  background: 'rgba(30, 107, 104, 0.05)',
                  borderRadius: '12px'
                }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#999' }}>Mood</span>
                    <div style={{ fontWeight: '600', color: '#1E6B68' }}>{entry.mood}/10</div>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#999' }}>Energy</span>
                    <div style={{ fontWeight: '600', color: '#1E6B68' }}>{entry.energy}/10</div>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#999' }}>Focus</span>
                    <div style={{ fontWeight: '600', color: '#1E6B68' }}>{entry.focus}/10</div>
                  </div>
                </div>

                    {/* Entry Text Snippet */}
                    {entry.entry_text && (
                      <p style={{
                        color: '#666',
                        lineHeight: '1.6',
                        fontSize: '0.95rem',
                        marginBottom: '8px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {entry.entry_text}
                      </p>
                    )}

                    {/* The mirror — persisted reflection, turns the journal
                        into an ongoing dialogue rather than a monologue */}
                    {entry.mirror && (
                      <div style={{
                        marginTop: '10px', padding: '10px 14px',
                        background: 'rgba(30,107,104,0.045)',
                        borderLeft: '2px solid rgba(30,107,104,0.3)',
                        borderRadius: '0 10px 10px 0',
                      }}>
                        <span style={{
                          display: 'block', fontSize: '0.58rem', fontWeight: '700',
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: '#2D8B87', marginBottom: '3px',
                        }}>Reflected back</span>
                        <span style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: '0.9rem', color: '#3A5754', lineHeight: 1.55,
                        }}>{entry.mirror}</span>
                      </div>
                    )}

                    {/* Synchronicity Badge */}
                    {entry.synchronicity && (
                      <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: 'rgba(217, 203, 163, 0.3)',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        color: '#1E6B68',
                        fontWeight: '500'
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="11" height="11" viewBox="0 0 20 20" fill="none"><path d="M10 2 L11.5 8 L17 8 L12.5 11.5 L14.5 17.5 L10 14 L5.5 17.5 L7.5 11.5 L3 8 L8.5 8 Z" stroke="#1E6B68" strokeWidth="1.4" strokeLinejoin="round"/></svg>
                          Synchronicity noted
                        </span>
                      </div>
                    )}

                    {/* Expanded detail */}
                    {expandedEntryId === entry.id && (
                      <div style={{
                        marginTop: '20px',
                        paddingTop: '20px',
                        borderTop: '1px solid rgba(30,107,104,0.1)',
                        display: 'flex', flexDirection: 'column', gap: '14px'
                      }} onClick={e => e.stopPropagation()}>
                        {[
                          { label: 'Body sensation', value: entry.body_sensation },
                          { label: 'Activation location', value: entry.activation_location },
                          { label: 'What emerged', value: entry.what_emerged },
                          { label: 'Integration insight', value: entry.integration_insight },
                          { label: 'Reflection', value: entry.entry_text && !entry.body_sensation ? entry.entry_text : null }
                        ].filter(f => f.value && f.value.trim()).map(({ label, value }) => (
                          <div key={label}>
                            <p style={{ margin: '0 0 4px', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#2D8B87' }}>
                              {label}
                            </p>
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#444', lineHeight: '1.6' }}>
                              {value}
                            </p>
                          </div>
                        ))}
                        <button
                          style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: '#9AADAB', fontSize: '0.8rem', cursor: 'pointer', padding: '4px 0' }}
                          onClick={() => setExpandedEntryId(null)}
                        >
                          ↑ collapse
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
          
          {/* Pattern Intelligence */}
          {!showForm && user?.user_id && (
            <PatternIntelligencePanel userId={user.user_id} />
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default MindMirror;